import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import { icmTruncationNote } from '../icmPaging';
import { DateFilterSelect, monthsOf, DEFAULT_DATE_FILTER, type DateFilterId }
  from '../operations/IcmDateFilter';
import { fetchCancelledRequisition, type CancelledRequisitionRow } from './icmTrackingApi';
import '../../backlog/backlog.css';
import '../alerts/icmAlerts.css';

/**
 * Inventory Management → Tracking → Cancelled Requisition.
 *
 * Ported from ALPACA `Requisition_ListView_Cancelled` (criteria
 * `[Status] = Cancelled`, enum value 7), which is read-only. The SP keys on
 * `Status = 7` and sorts by Requested Date descending like the legacy model —
 * see `AI_ICM_TrackingCancelledRequisition_List_SP`.
 *
 * DATE FILTER (2026-10-05). Legacy ALSO applies the ICM date window here, from
 * `RequisitionViewcontroller.OnActivated` rather than the model, defaulting to
 * `DefaultSetting.InventoryWorkFlow` (= 3M for this lab) — which is why G5 showed
 * 2 rows where AI showed all 185. ONE DELIBERATE DEVIATION: legacy renders NO chip
 * on this view (`RequisitionDateFilter.TargetViewId` is only ViewMode + Review),
 * so its 3-month window is invisible and the older rows are simply unreachable.
 * AI shows the shared picker, defaulted to the same 3M, so the default matches G5
 * row-for-row while the user can still widen to ALL.
 * Legacy sets `ShowSelectionColumn=False` on this view, but a
 * row-selection checkbox column is shown here on user request (2026-09-22)
 * for consistency with the other Tracking grids.
 */
export default function CancelledRequisitionPage() {
  const [rows, setRows] = useState<CancelledRequisitionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilterId>(DEFAULT_DATE_FILTER);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const page = await fetchCancelledRequisition(undefined, monthsOf(dateFilter));
      setRows(page.rows);
      setTotal(page.total);
      setSelected(new Set());
    } catch {
      setError('Failed to load cancelled requisitions.');
      setRows([]); setTotal(0);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.rqid ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.itemCode ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t) ||
      (r.canceledByName ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  // Column set + order from the LIVE ALPACA model (Requisition_ListView_Cancelled):
  // Date · Item · Description · Catalog# · Vendor · OrderQty · PackUnits ·
  // DeliveryPriority · Department · RequestedBy · CanceledDate · CanceledBy.
  // Captions are G5's. RQ# is not a visible column in G5, so it is not shown here,
  // though the search below still finds a requisition by it (Bug 23).
  const cols: AppColumn<CancelledRequisitionRow>[] = [
    /* DATE ONLY. The legacy column carries an explicit
       DisplayFormat="{0:d}" (Requisition_ListView_Cancelled, the Date column), so
       GoLive renders the date and never the time. AI used fmtLocalDateTime,
       which also meant the 1,339 of 1,476 requisitions stored at exactly
       midnight rendered as "04/20/2026 00:00" -- a time nobody recorded, shown
       as though it were. The other four requisition grids already use
       fmtLocalDate. */
    { field: 'requestedDate', header: 'Date',        width: 110, type: 'date', text: r => fmtLocalDate(r.requestedDate) ?? '—', render: r => fmtLocalDate(r.requestedDate) ?? '—' },
    { field: 'itemName',      header: 'Item',        width: 220, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'specification', header: 'Description',   width: 220, text: r => r.specification ?? '—', render: r => r.specification ?? '—' },
    { field: 'catalog',       header: 'Catalog#',    width: 120, text: r => r.catalog ?? '—', render: r => r.catalog ?? '—' },
    { field: 'vendorName',    header: 'Vendor',      width: 150, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'orderQty',      header: 'OrderQty',    width: 100, type: 'number', align: 'right', text: r => String(r.orderQty), value: r => r.orderQty },
    { field: 'packUnit',      header: 'PackUnits',   width: 100, text: r => r.packUnit ?? '—', render: r => r.packUnit ?? '—' },
    { field: 'deliveryPriorityName', header: 'DeliveryPriority',  width: 140, text: r => r.deliveryPriorityName ?? '—', render: r => r.deliveryPriorityName ?? '—' },
    { field: 'departmentName', header: 'Department', width: 140, text: r => r.departmentName ?? '—', render: r => r.departmentName ?? '—' },
    { field: 'requestedByName', header: 'RequestedBy',  width: 150, text: r => r.requestedByName ?? '—', render: r => r.requestedByName ?? '—' },
    /* Canceled Date KEEPS the time: unlike Date, this column carries NO
       DisplayFormat in the model and none on the BO either, so XAF falls back
       to its default DateTime rendering, which includes it. */
    { field: 'canceledDate',  header: 'CanceledDate',  width: 150, type: 'date', text: r => fmtLocalDateTime(r.canceledDate) ?? '—', render: r => fmtLocalDateTime(r.canceledDate) ?? '—' },
    { field: 'canceledByName', header: 'CanceledBy',  width: 150, text: r => r.canceledByName ?? '—', render: r => r.canceledByName ?? '—' },
  ];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Inventory Management</span><i className="ti ti-chevron-right" />
            <span>Tracking</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Cancelled Requisition</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-cancelled-requisition" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={<DateFilterSelect value={dateFilter} onChange={setDateFilter} />}
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'cancelled-requisition', ...gridExportMatrix(filtered, cols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search RQ#, item, vendor, canceled by…"
              data-unsaved-ignore
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span className="ica-note">{total.toLocaleString()} cancelled requisition(s)</span>
          {icmTruncationNote(rows.length, total) && (
            <span className="ica-note ica-note-warn">{icmTruncationNote(rows.length, total)}</span>
          )}
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<CancelledRequisitionRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              defaultPageSize={20}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText="No cancelled requisitions found."
            />
          )}
        </div>
      </div>
    </div>
  );
}
